import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { stackAPI, userAPI } from '../services/api';
import { getPositionInputStyles } from '../utils/stack';
import { apiErrorMessage } from '../utils/apiError';
import { confirmAction, acknowledgeAction } from '../utils/confirmationDialog';
import { BUTTON_COLORS } from '../utils/buttonColors';
import { getDiceBearUserAvatar, getDiceBearGroupAvatar } from '../services/dicebear';
import CharacterCounter from '../utils/characterCounter';

const STACK_COLORS = ['var(--stack-1)', 'var(--stack-2)', 'var(--stack-3)', 'var(--stack-4)', 'var(--stack-5)', 'var(--stack-6)'];
const STACK_TEXT_COLORS = ['var(--text-on-stack-1)', 'var(--text-on-stack-2)', 'var(--text-on-stack-3)', 'var(--text-on-stack-4)', 'var(--text-on-stack-5)', 'var(--text-on-stack-6)'];

// Helper function to safely extract string value from position (handles both string and object cases)
const getPositionValue = (pos) => {
  if (typeof pos === 'string') {
    return pos;
  }
  if (pos && typeof pos === 'object' && pos.name) {
    return pos.name;
  }
  return '';
};

const CreateStack = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  mode, // 'friend' or 'group'
  targetId, // friend_id or group_id
  targetName, // friend username or group name
  targetAvatar, // friend or group avatar URL
  title = null, // custom title, defaults to "New {Friend/Group} Stack"
  showDraftButtons = true, // whether to show Save Draft and Make Collab buttons
  // New props for collaborative editing
  editingStack = null, // stack object when editing an existing collaborative stack
  isDraftEdit = false, // whether this is editing a draft stack
  isCollabEdit = false, // whether this is editing a collaborative stack
}) => {
  const [form, setForm] = useState({ name: '', positions: Array(6).fill('') });
  const [positionUrls, setPositionUrls] = useState(Array(6).fill(null)); // Track URLs for each position
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  // URL dialog state
  const [urlDialog, setUrlDialog] = useState({ isOpen: false, positionIndex: null, urlValue: '' });
  
  // Target selection state
  const [showTargetSelection, setShowTargetSelection] = useState(false);
  const [availableTargets, setAvailableTargets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);

  // Load targets for selection
  const loadTargets = useCallback(async () => {
    try {
      setLoadingTargets(true);
      let targets = [];
      
      if (mode === 'friend') {
        const resp = await userAPI.getMyFriends();
        targets = (resp.data || []).map(friend => ({
          id: friend.id,
          name: friend.username,
          avatar: getDiceBearUserAvatar(friend.username, friend.avatar_id)
        }));
      } else if (mode === 'group') {
        const resp = await userAPI.getMyGroups();
        targets = (resp.data || []).map(group => ({
          id: group.id,
          name: group.name,
          avatar: getDiceBearGroupAvatar(group.name)
        }));
      }
      
      setAvailableTargets(targets);
    } catch (err) {
      console.error('Failed to load targets:', err);
      setError(`Failed to load ${mode}s`);
    } finally {
      setLoadingTargets(false);
    }
  }, [mode]);

  // Handle target selection when dialog opens
  useEffect(() => {
    if (isOpen && !targetId && !editingStack) {
      setSelectedTarget(null); // Clear any previous selection
      setShowTargetSelection(true);
      loadTargets();
    } else if (isOpen && targetId) {
      setShowTargetSelection(false);
      setSelectedTarget({
        id: targetId,
        name: targetName,
        avatar: targetAvatar
      });
    } else if (isOpen && editingStack) {
      setShowTargetSelection(false);
      // Extract target info from editingStack
      if (editingStack.audience?.user) {
        setSelectedTarget({
          id: editingStack.audience.user.id,
          name: editingStack.audience.user.username,
          avatar: getDiceBearUserAvatar(editingStack.audience.user.username, editingStack.audience.user.avatar_id)
        });
      } else if (editingStack.audience?.group) {
        setSelectedTarget({
          id: editingStack.audience.group.id,
          name: editingStack.audience.group.name,
          avatar: getDiceBearGroupAvatar(editingStack.audience.group.name)
        });
      }
    }
  }, [isOpen, targetId, targetName, targetAvatar, editingStack, loadTargets]);

  const handleTargetSelect = (target) => {
    setSelectedTarget(target);
    setShowTargetSelection(false);
  };

  // Helper function to extract URL from position (handles both string and object cases)
  const getPositionUrl = (pos) => {
    if (typeof pos === 'string') {
      return null;
    }
    if (pos && typeof pos === 'object' && pos.url) {
      return pos.url;
    }
    return null;
  };

  // Populate form when editing a collaborative stack
  useEffect(() => {
    if (isOpen && (isCollabEdit || editingStack) && editingStack) {
      // Get positions from the stack - they might be null for empty positions
      let positions = editingStack.positions || [];
      
      // Convert all positions to strings using the helper function
      const stringPositions = positions.map(p => getPositionValue(p));
      
      const paddedPositions = Array(6).fill('').map((_, index) => stringPositions[index] || '');
      
      // Extract URLs from positions
      const urls = Array(6).fill(null).map((_, index) => {
        const pos = positions[index];
        return pos ? getPositionUrl(pos) : null;
      });
      
      setForm({
        name: editingStack.name || '',
        positions: paddedPositions
      });
      setPositionUrls(urls);
    } else if (isOpen && !editingStack) {
      // Reset form for new stack creation
      setForm({ name: '', positions: Array(6).fill('') });
      setPositionUrls(Array(6).fill(null));
    }
    setError('');
  }, [isOpen, isCollabEdit, editingStack]);

  // Validation logic
  const isFormValid = useMemo(() => {
    if (form.name && form.name.length > 48) return false;
    
    // For collaborative editing or draft editing, only validate position lengths, allow empty positions
    if (isCollabEdit || isDraftEdit) {
      if (form.positions.some((p) => p && p.length > 48)) return false;
    } else {
      // For new stacks, all positions must be filled
      if (form.positions.some((p) => !p || p.length === 0 || p.length > 48)) return false;
    }
    return true;
  }, [form, isCollabEdit, isDraftEdit]);

  // Validation for Save Draft button - allows empty positions for drafts
  const isSaveDraftValid = useMemo(() => {
    if (form.name && form.name.length > 48) return false;
    
    // For drafts, only validate position lengths, allow empty positions
    if (form.positions.some((p) => p && p.length > 48)) return false;
    
    return true;
  }, [form]);

  // Validation for Create Stack button - requires all positions, stack name optional for Friend/Group
  const isCreateStackValid = useMemo(() => {
    // For Friend and Group stacks, stack name is optional
    if (mode === 'friend' || mode === 'group') {
      if (form.name && form.name.length > 48) return false;
    } else {
      // For other stack types, stack name is required
      if (!form.name || form.name.trim().length === 0) return false;
      if (form.name && form.name.length > 48) return false;
    }
    
    // All positions must be filled for Create Stack
    if (form.positions.some((p) => !p || p.length === 0 || p.length > 48)) return false;
    
    return true;
  }, [form, mode]);

  const hasDuplicatePositions = useMemo(() => {
    try {
      const seen = new Set();
      for (const raw of form.positions || []) {
        const value = getPositionValue(raw).trim();
        if (!value) continue; // empties are already invalid via isValid
        if (seen.has(value)) return true;
        seen.add(value);
      }
      return false;
    } catch (_) {
      return false;
    }
  }, [form.positions]);

  // Validation for Make Collab button - requires stack name and at least one empty position
  const isMakeCollabValid = useMemo(() => {
    if (!form.name || form.name.trim().length === 0) return false;
    
    // Check if at least one position is empty (not all positions filled)
    const filledPositions = form.positions.filter(pos => getPositionValue(pos).trim().length > 0);
    return filledPositions.length < 6;
  }, [form.name, form.positions]);

  // Validation for Save Collab button - requires at least one new position
  const isSaveCollabValid = useMemo(() => {
    if (!isFormValid) return false;
    if (!isCollabEdit || !editingStack) return false;
    
    // Check if user has entered at least one new position
    const originalPositions = editingStack.positions || [];
    const hasNewPosition = form.positions.some((pos, index) => {
      const originalPos = originalPositions[index];
      const originalValue = getPositionValue(originalPos);
      const currentValue = getPositionValue(pos);
      return currentValue && currentValue.trim() && currentValue.trim() !== originalValue;
    });
    
    return hasNewPosition;
  }, [isFormValid, isCollabEdit, editingStack, form.positions]);

  const resetForm = useCallback(() => {
    setForm({ name: '', positions: Array(6).fill('') });
    setPositionUrls(Array(6).fill(null));
    setError('');
    setSelectedTarget(null);
    setUrlDialog({ isOpen: false, positionIndex: null, urlValue: '' });
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // Validate URL format
  const isValidUrl = (urlString) => {
    if (!urlString || urlString.trim() === '') return false;
    try {
      const url = new URL(urlString.trim());
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Handle closing URL dialog
  const handleCloseUrlDialog = useCallback(() => {
    setUrlDialog({ isOpen: false, positionIndex: null, urlValue: '' });
    setError(''); // Clear any URL validation errors
  }, []);

  // Handle opening URL dialog
  const handleOpenUrlDialog = (positionIndex) => {
    setUrlDialog({
      isOpen: true,
      positionIndex,
      urlValue: positionUrls[positionIndex] || ''
    });
  };

  // Handle escape key to close dialog
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        // If URL dialog is open, close it first
        if (urlDialog.isOpen) {
          handleCloseUrlDialog();
        } else {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleClose, urlDialog.isOpen, handleCloseUrlDialog]);

  // Help mobile browsers keep focused inputs visible when the virtual keyboard opens
  const onMobileFocus = useCallback((event) => {
    try {
      event.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // Run again shortly after to catch viewport resize after keyboard shows
      setTimeout(() => {
        try { event.target.scrollIntoView({ block: 'center' }); } catch (_) {}
      }, 250);
    } catch (_) {}
  }, []);

  // Handle saving URL
  const handleSaveUrl = () => {
    const { positionIndex, urlValue } = urlDialog;
    if (positionIndex === null) return;
    
    const trimmedUrl = urlValue.trim();
    if (trimmedUrl === '') {
      // Empty string becomes null
      const newUrls = [...positionUrls];
      newUrls[positionIndex] = null;
      setPositionUrls(newUrls);
      handleCloseUrlDialog();
      return;
    }
    
    // Validate URL
    if (!isValidUrl(trimmedUrl)) {
      setError('Invalid URL format. Please include http:// or https://');
      // Don't close dialog on validation error
      return;
    }
    
    const newUrls = [...positionUrls];
    newUrls[positionIndex] = trimmedUrl;
    setPositionUrls(newUrls);
    setError(''); // Clear any previous errors
    handleCloseUrlDialog();
  };

  // Handle clearing URL
  const handleClearUrl = () => {
    const { positionIndex } = urlDialog;
    if (positionIndex === null) return;
    
    const newUrls = [...positionUrls];
    newUrls[positionIndex] = null;
    setPositionUrls(newUrls);
    handleCloseUrlDialog();
  };

  const handleCreate = async (targetStatus = 'published') => {
    try {
      if (hasDuplicatePositions) {
        setError('Identical positions not supported.');
        return;
      }
      setCreating(true);
      
      // Convert positions to objects with name and url
      const positionsWithUrls = form.positions.map((name, index) => {
        const url = positionUrls[index];
        if (!name || name.trim() === '') {
          return null;
        }
        return {
          name: name.trim(),
          url: url || null  // Always include url field, even if null
        };
      });
      
      const payload = {
        name: form.name?.trim() || null,
        positions: positionsWithUrls,
      };
      
      let resp;
      
      // Check if we're editing an existing collaborative stack first
      if (editingStack && isCollabEdit) {
        // Update existing collaborative stack
        resp = await stackAPI.collaborateStack(editingStack.id, payload);
      } else if (editingStack && isDraftEdit) {
        if (targetStatus === 'draft') {
          // Update existing draft stack
          resp = await stackAPI.updateDraft(editingStack.id, payload);
        } else if (targetStatus === 'published') {
          // Publish existing draft stack
          resp = await stackAPI.publishStack(editingStack.id, payload);
        }
      } else {
        // Create new stack (including new drafts)
        const createPayload = {
          ...payload,
          mode,
          target_id: selectedTarget?.id || targetId,
          target_status: targetStatus,
        };
        resp = await stackAPI.createStack(createPayload);
      }
      
      resetForm();
      onClose();
      if (onSuccess) onSuccess(resp.data);
    } catch (err) {
      setError(apiErrorMessage(
        err,
        `Failed to ${targetStatus === 'draft' ? 'save draft' : (editingStack && isDraftEdit ? 'publish stack' : 'create stack')}`
      ));
      console.error('Stack creation error:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      const confirmed = await confirmAction(
        'Save Draft',
        'This will save your stack as a draft. You can come back to it later and continue editing. Only you will be able to see and edit it until you publish it.',
        {
          confirmText: 'Save Draft',
          cancelText: 'Cancel',
          confirmButtonColor: BUTTON_COLORS.DRAFT,
          cancelButtonColor: BUTTON_COLORS.CANCEL,
        }
      );
      
      if (!confirmed) return;
      await handleCreate('draft');
    } catch (err) {
      setError('Failed to save draft');
      console.error(err);
    }
  };

  const handleMakeCollab = async () => {
    try {
      setCreating(true);
      setError('');
      
      // Convert positions to objects with name and url
      const positionsWithUrls = form.positions.map((name, index) => {
        const url = positionUrls[index];
        if (!name || name.trim() === '') {
          return null;
        }
        return {
          name: name.trim(),
          url: url || null  // Always include url field, even if null
        };
      });
      
      const payload = {
        name: form.name.trim(),
        positions: positionsWithUrls,
      };
      
      let response;
      
      // Check if we're editing an existing stack (collaborative or draft)
      if (editingStack && (isCollabEdit || isDraftEdit)) {
        // Update existing stack (collaborative or convert draft to collaborative)
        response = await stackAPI.collaborateStack(editingStack.id, payload);
      } else {
        // For "Make Collab" button on new stacks, create a new collaborative stack
        const collabPayload = {
          name: form.name.trim(),
          mode: mode, // 'friend' or 'group'
          positions: positionsWithUrls,
          target_status: 'collab'
        };
        
        // Add target_id if we have a selected target
        if (selectedTarget) {
          collabPayload.target_id = selectedTarget.id;
        } else if (targetId) {
          collabPayload.target_id = targetId;
        }
        
        response = await stackAPI.createStack(collabPayload);
      }
      
      await acknowledgeAction(
        'Collaborative Stack Created',
        'Your collaborative stack has been created! You or other members of the stack can now add to the open positions.',
        {
          confirmText: 'OK',
          confirmButtonColor: BUTTON_COLORS.SUCCESS,
        }
      );
      
      onSuccess?.(response.data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create collaborative stack'));
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleSaveCollab = async () => {
    try {
      if (hasDuplicatePositions) {
        setError('Identical positions not supported.');
        return;
      }
      
      setCreating(true);
      
      // Check if all positions are now filled
      const allFilled = form.positions.every(pos => getPositionValue(pos).trim());
      
      if (allFilled) {
        // Show confirmation dialog for publishing
        const confirmed = await confirmAction(
          'Publish Collaborative Stack',
          'All positions are now filled! This will publish the completed stack and redirect you to view it.',
          {
            confirmText: 'Publish Stack',
            cancelText: 'Cancel',
            confirmButtonColor: BUTTON_COLORS.SUCCESS,
            cancelButtonColor: BUTTON_COLORS.CANCEL,
          }
        );
        
        if (!confirmed) {
          setCreating(false);
          return;
        }
      }
      
      // Convert positions to objects with name and url
      const positionsWithUrls = form.positions.map((name, index) => {
        const url = positionUrls[index];
        const trimmedName = name?.trim() || null;
        if (!trimmedName) {
          return null;
        }
        return {
          name: trimmedName,
          url: url || null  // Always include url field, even if null
        };
      });
      
      const payload = {
        name: form.name?.trim() || null,
        positions: positionsWithUrls,
      };
      
      const resp = await stackAPI.collaborateStack(editingStack.id, payload);
      resetForm();
      onClose();
      
      if (allFilled && onSuccess) {
        // Redirect to the stack profile if it's now complete
        onSuccess(resp.data);
      } else if (onSuccess) {
        // Just refresh the stacks list
        onSuccess(resp.data);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save collaborative changes'));
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  const dialogTitle = title || (isCollabEdit ? 'Add to Collab Stack' : `New ${mode === 'friend' ? 'Friend' : 'Group'} Stack`);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--overlay)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      zIndex: 10000,
      overflowY: 'auto',
      padding: 'env(safe-area-inset-top, 16px) 16px 16px'
    }}>
      <div style={{
        backgroundColor: 'var(--color-surface)',
        color: 'var(--color-text)',
        padding: '20px 32px',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 4px 8px var(--color-shadow)',
        maxHeight: '90dvh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>{dialogTitle}</h3>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 1,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-border)';
              e.target.style.color = 'var(--color-text)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'var(--color-text-muted)';
            }}
            title="Close dialog (Esc)"
          >
            ×
          </button>
        </div>
        {isCollabEdit && (
          <div style={{ marginBottom: '8px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
            <em>
              Collab stacks are built by multiple users. Fill out any number of open positions and hit "Save Collab" to contribute!
            </em>
          </div>
        )}
        {error && (
          <div className="error-message" style={{ marginBottom: '12px' }}>
            {typeof error === 'string' ? error : 'An error occurred'}
          </div>
        )}
        
        {/* Target Selection Interface */}
        {showTargetSelection && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px' }}>Select a {mode === 'friend' ? 'Friend' : 'Group'}</h4>
            {loadingTargets ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>Loading {mode}s...</div>
            ) : availableTargets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-text-muted)' }}>
                No {mode}s available. Please add some {mode}s first.
              </div>
            ) : (
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto', 
                border: '1px solid var(--color-border)', 
                borderRadius: '6px',
                padding: '10px'
              }}>
                {availableTargets.map((target) => (
                  <div
                    key={target.id}
                    onClick={() => handleTargetSelect(target)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--color-background)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <img 
                      src={target.avatar} 
                      alt={`${target.name} avatar`}
                      style={{ width: '30px', height: '30px', borderRadius: '50%' }}
                    />
                    <span style={{ fontWeight: 'bold' }}>{target.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Stack Name - only show after target selection */}
        {((selectedTarget || (targetId && targetName && targetAvatar)) || editingStack) && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Stack Name (max 48 characters){mode === 'friend' || mode === 'group' ? ' - optional' : ''}:
            </label>
          <input 
            type="text" 
            value={form.name} 
            maxLength={48} 
            onFocus={onMobileFocus} 
            onChange={(e) => setForm({ ...form, name: e.target.value })} 
            readOnly={isCollabEdit}
            style={{ 
              width: '100%', 
              padding: '8px', 
              border: '1px solid var(--color-border)', 
              borderRadius: '4px', 
              background: isCollabEdit ? 'var(--color-background)' : 'var(--color-surface)', 
              color: isCollabEdit ? 'var(--color-text-secondary)' : 'var(--color-text)', 
              fontSize: '16px',
              cursor: isCollabEdit ? 'not-allowed' : 'text'
            }} 
          />
          <CharacterCounter current={form.name.length} max={48} />
          </div>
        )}

        {/* Positions - only show after target selection */}
        {((selectedTarget || (targetId && targetName && targetAvatar)) || editingStack) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', marginBottom: '12px' }}>
          {Array.from({ length: 6 }).map((_, idx) => {
            const isExistingPosition = isCollabEdit && editingStack && editingStack.positions && editingStack.positions[idx] && 
              (typeof editingStack.positions[idx] === 'string' ? editingStack.positions[idx].trim() : editingStack.positions[idx]?.name?.trim());
            // Find contributor from editingStack.collab who authored this position (idx+1)
            const contributor = (isExistingPosition && editingStack && Array.isArray(editingStack.collab))
              ? editingStack.collab.find(c => Array.isArray(c.positions) && c.positions.includes(idx + 1))
              : null;
            const hasUrl = positionUrls[idx] !== null;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input 
                  type="text" 
                  placeholder={`Position ${idx + 1}`} 
                  value={getPositionValue(form.positions[idx])} 
                  maxLength={48} 
                  onFocus={onMobileFocus} 
                  onChange={(e) => {
                    const positions = [...form.positions];
                    positions[idx] = e.target.value;
                    setForm({ ...form, positions });
                  }} 
                  readOnly={isExistingPosition}
                  style={{
                    flex: 1,
                    ...getPositionInputStyles(form.positions[idx], STACK_TEXT_COLORS[idx], STACK_COLORS[idx]),
                    ...(isExistingPosition ? {
                      background: 'var(--color-background)',
                      color: 'var(--color-text-secondary)',
                      border: `2px solid ${STACK_COLORS[idx]}`, // Add colored border for position
                      cursor: 'not-allowed'
                    } : {})
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleOpenUrlDialog(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '20px',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  title={hasUrl ? `Edit URL: ${positionUrls[idx]}` : 'Set URL'}
                  disabled={isExistingPosition}
                >
                  {hasUrl ? '🔗' : '⛓️‍💥'}
                </button>
                {isExistingPosition && contributor && (
                  <img
                    src={getDiceBearUserAvatar(contributor.username, contributor.avatar_id)}
                    alt={contributor.username}
                    title={contributor.username}
                    style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                  />
                )}
              </div>
            );
          })}
          </div>
        )}

        {/* Target Display - only show after target selection */}
        {(selectedTarget || (targetId && targetName && targetAvatar)) && (
          <div style={{ 
            border: '1px solid var(--color-border)', 
            borderRadius: '6px', 
            padding: '10px', 
            marginBottom: '12px',
            backgroundColor: 'var(--color-surface)'
          }}>
            <div style={{ marginBottom: '10px', color: 'var(--color-text-muted)' }}>
              {isCollabEdit ? 'Collaborating on stack for' : 'Creating stack for'} {mode === 'friend' ? 'friend' : 'group'}:
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={selectedTarget?.avatar || targetAvatar} 
                alt={`${selectedTarget?.name || targetName} avatar`}
                style={{ width: '30px', height: '30px', borderRadius: '50%' }}
              />
              <span style={{ fontWeight: 'bold' }}>{selectedTarget?.name || targetName}</span>
            </div>
          </div>
        )}

        {/* Action Buttons - only show after target selection */}
        {((selectedTarget || (targetId && targetName && targetAvatar)) || editingStack) && (
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button 
            className="btn" 
            style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text)' }} 
            onClick={handleClose}
          >
            Cancel
          </button>
          
          {isCollabEdit ? (
            // Collaborative editing buttons
            <button 
              className="btn" 
              style={{ backgroundColor: BUTTON_COLORS.COLLAB, color: 'white' }}
              onClick={handleSaveCollab}
              disabled={creating || !isSaveCollabValid}
            >
              {creating ? 'Saving...' : 'Save Collab'}
            </button>
          ) : (
            // New stack creation buttons
            <>
              {showDraftButtons && (
                <>
                  <button 
                    className="btn" 
                    style={{ backgroundColor: BUTTON_COLORS.DRAFT, color: 'white' }}
                    onClick={handleSaveDraft}
                    disabled={creating || !isSaveDraftValid}
                  >
                    Save Draft
                  </button>
                  
                  <button 
                    className="btn" 
                    style={{ backgroundColor: BUTTON_COLORS.COLLAB, color: 'white' }}
                    onClick={handleMakeCollab}
                    disabled={creating || !isMakeCollabValid}
                  >
                    Make Collab
                  </button>
                </>
              )}
              
              {(() => {
                const createDisabled = creating || !isCreateStackValid;
                return (
                  <button 
                    className="btn" 
                    aria-disabled={createDisabled} 
                    disabled={createDisabled} 
                    style={{ 
                      backgroundColor: createDisabled ? 'var(--color-border)' : BUTTON_COLORS.SUCCESS, 
                      color: createDisabled ? 'var(--color-text)' : '#fff', 
                      cursor: createDisabled ? 'not-allowed' : 'pointer', 
                      opacity: createDisabled ? 0.8 : 1 
                    }} 
                    onClick={() => !createDisabled && handleCreate('published')}
                  >
                    {creating ? 'Creating...' : 'Create Stack'}
                  </button>
                );
              })()}
            </>
          )}
          </div>
        )}
      </div>
      
      {/* URL Dialog */}
      {urlDialog.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--overlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            padding: '20px 32px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 8px var(--color-shadow)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Set URL link</h3>
            {error && error.includes('URL') && (
              <div className="error-message" style={{ marginBottom: '12px', fontSize: '14px' }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px' }}>
                URL:
              </label>
              <input
                type="text"
                value={urlDialog.urlValue}
                onChange={(e) => {
                  setUrlDialog({ ...urlDialog, urlValue: e.target.value });
                  // Clear URL-specific errors when user types
                  if (error && error.includes('URL')) {
                    setError('');
                  }
                }}
                placeholder="https://example.com"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveUrl();
                  } else if (e.key === 'Escape') {
                    handleCloseUrlDialog();
                  }
                }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn"
                style={{ backgroundColor: BUTTON_COLORS.PRIMARY, color: 'white' }}
                onClick={handleCloseUrlDialog}
              >
                Back
              </button>
              <button
                className="btn"
                style={{ backgroundColor: BUTTON_COLORS.WARNING, color: 'black' }}
                onClick={handleClearUrl}
              >
                Clear
              </button>
              <button
                className="btn"
                style={{ backgroundColor: BUTTON_COLORS.SUCCESS, color: 'white' }}
                onClick={handleSaveUrl}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateStack;
